import { KgInsightThemeType } from '@api/api/kg-insights';
import { highlight } from '@helpers/utils/highlight/highlight';
import { ThemeSearchLogo } from '@terminal/components/theme-search-logo/ThemeSearchLogo';
import { useTheme } from '@terminal/react-query-hooks/themes/useThemeOverview';
import { ThemeCompanyCount } from '@terminal/views/theme-overview/components/theme-company-count/ThemeCompanyCount';
import { ThemeSubtitle } from '@terminal/views/theme-overview/components/theme-subtitle/ThemeSubtitle';
import clsx from 'clsx';

import { Theme } from '../../utils';
import styles from './ListThemeItem.module.scss';

export interface ListThemeItemProps {
  theme: Theme;
  onFocus?: (name: string) => void;
  highlighted?: string;
  isFocused: boolean;
  companyCountTypes?: KgInsightThemeType[];
}

export const ListThemeItem = ({
  theme,
  onFocus,
  highlighted,
  isFocused,
  companyCountTypes,
}: ListThemeItemProps) => {
  const { data } = useTheme({
    themeId: theme.id,
  });

  const logoUrl = data?.logo_url;
  // Recents persisted before `type` was stored carry no `theme.type`, so the
  // restriction has to run against the resolved type, not the passed-in item.
  const type = theme.type ?? data?.type;
  const showCompanyCount =
    !companyCountTypes || (!!type && companyCountTypes.includes(type));

  const themeName = highlighted
    ? highlight(theme.name, highlighted)
    : theme.name;

  return (
    <button
      className={clsx(styles.listThemeItemRoot, {
        [styles.isFocused]: isFocused,
      })}
      data-testid={`list-theme-item-${theme.id}`}
      onFocus={() => onFocus?.(theme.name)}
    >
      <ThemeSearchLogo name={theme.name} logoUrl={logoUrl} themeType={type} />
      <div className={styles.details}>
        <span className={styles.title}>{themeName}</span>
        <span className={styles.subtitle}>
          <ThemeSubtitle type={type} />
          {showCompanyCount && (
            <ThemeCompanyCount themeId={theme.id} themeType={type} />
          )}
        </span>
      </div>
    </button>
  );
};
